import React from "react";

type ValidateReferralResponse =
	| { valid: true; referralType: "friend" | "group"; originatorName: string }
	| { valid: false; reason: "expired" | "redeemed" | "revoked" | "not_found" };

interface ReferralBannerProps {
	referralInfo: ValidateReferralResponse;
}

const PersonIcon: React.FC<{ className?: string }> = ({ className }) => (
	<svg
		className={className}
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<circle cx="12" cy="8" r="4" />
		<path d="M4 20c0-4 4-7 8-7s8 3 8 7" />
	</svg>
);

const GroupIcon: React.FC<{ className?: string }> = ({ className }) => (
	<svg
		className={className}
		width="32"
		height="24"
		viewBox="0 0 32 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<circle cx="10" cy="8" r="3" />
		<path d="M3 20c0-3.5 3-6 7-6s7 2.5 7 6" />
		<circle cx="22" cy="8" r="3" />
		<path d="M18 20c0-3.5 2-5 4-5s4 1.5 4 5" />
	</svg>
);

const ClockIcon: React.FC<{ className?: string }> = ({ className }) => (
	<svg
		className={className}
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<circle cx="12" cy="12" r="10" />
		<path d="M12 6v6l4 2" />
	</svg>
);

const SparkleIcon: React.FC<{ className?: string }> = ({ className }) => (
	<svg
		className={className}
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8L12 2z" />
	</svg>
);

const ReferralBanner: React.FC<ReferralBannerProps> = ({ referralInfo }) => {
	if (referralInfo.valid) {
		const isFriend = referralInfo.referralType === "friend";
		return (
			<div className="border border-primary-active rounded-xl p-4 mb-4 bg-primary-accent">
				<div className="flex items-center gap-3">
					{isFriend ? (
						<PersonIcon className="text-green-400 flex-shrink-0" />
					) : (
						<GroupIcon className="text-green-400 flex-shrink-0" />
					)}
					<div>
						<p className="text-primary-active font-semibold">
							{isFriend
								? `You've been invited by ${referralInfo.originatorName}!`
								: referralInfo.originatorName}
						</p>
						<p className="text-primary-active text-sm">
							Subscribe to JobMap and earn bonus credits.
						</p>
						<p className="text-primary-inactive text-xs">
							(Requires a paid plan; free trial does not qualify.)
						</p>
					</div>
				</div>
			</div>
		);
	}

	if (referralInfo.reason === "revoked") {
		return (
			<div className="border border-primary-active rounded-xl p-4 mb-4 bg-primary-accent">
				<div className="flex items-center gap-3">
					<SparkleIcon className="text-green-400 flex-shrink-0" />
					<div>
						<p className="text-primary-active font-semibold">
							You've been invited to try JobMap!
						</p>
						<p className="text-primary-active text-sm">
							Subscribe to start organizing your job search.
						</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="border border-primary-inactive rounded-xl p-4 mb-4 bg-primary-accent">
			<div className="flex items-center gap-3">
				<ClockIcon className="text-primary-inactive flex-shrink-0" />
				<div>
					<p className="text-primary-active font-semibold">
						This referral code is no longer active.
					</p>
					<p className="text-primary-active text-sm">
						You can still sign up for JobMap!
					</p>
				</div>
			</div>
		</div>
	);
};

ReferralBanner.displayName = "ReferralBanner";

export default ReferralBanner;
export type { ReferralBannerProps, ValidateReferralResponse };
